import { useState, useEffect, useRef } from "react";
import { setEmotion } from "../api/books";

import { FICTION_GENRES } from "../data/constants";

const EmotionCoordinate = ({ book, currentUser, viewAsUserId, isOwned, users, onMemoAdded }) => {
  const [myEmotion, setMyEmotion] = useState(null);
  const [dragging, setDragging] = useState(false);
  const gridRef = useRef(null);
  const uid = currentUser?.uid;

  /** 화면 좌표 -> 0~100 좌표. 누른 자리에 그대로 찍힌다. */
  const place = (clientX, clientY) => {
    const el = gridRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const pct = (v, size) => Math.round(Math.min(100, Math.max(0, (v / size) * 100)));
    setMyEmotion({
      bright: pct(clientX - r.left, r.width),
      calm: pct(clientY - r.top, r.height),
    });
  };

  useEffect(() => {
    if (!book || !uid) return;
    const emotions = book.emotions || {};
    setMyEmotion(emotions[uid] || null);
  }, [book, uid]);

  const emotions = book.emotions || {};
  const allEmotions = Object.entries(emotions);
  const canEdit = isOwned && !viewAsUserId && !currentUser?.isAnonymous;
  const isFiction = FICTION_GENRES.includes(book.genre);
  const axisX = isFiction ? ["밝음","어두움"] : ["가벼움","무거움"];
  const axisY = isFiction ? ["차분","격렬"] : ["실용적","사색적"];

  return (
    <div className="emotion-section">
      <div className="emotion-plane">
        <div className="emotion-axis-label emotion-label-top">{axisY[0]}</div>
        <div className="emotion-axis-label emotion-label-bottom">{axisY[1]}</div>
        <div className="emotion-axis-label emotion-label-left">{axisX[0]}</div>
        <div className="emotion-axis-label emotion-label-right">{axisX[1]}</div>
        <div
          ref={gridRef}
          className={`emotion-grid${canEdit ? " emotion-grid-editable" : ""}`}
          onPointerDown={canEdit ? (e) => {
            e.currentTarget.setPointerCapture(e.pointerId);
            setDragging(true);
            place(e.clientX, e.clientY);
          } : undefined}
          onPointerMove={canEdit && dragging ? (e) => place(e.clientX, e.clientY) : undefined}
          onPointerUp={canEdit ? (e) => {
            e.currentTarget.releasePointerCapture(e.pointerId);
            setDragging(false);
          } : undefined}
          // 키보드로도 옮길 수 있어야 한다. 클릭만 되면 못 쓰는 사람이 생긴다.
          tabIndex={canEdit ? 0 : undefined}
          role={canEdit ? "application" : undefined}
          aria-label={canEdit ? `감정 좌표. 화살표 키로 이동. 가로 ${axisX[0]}에서 ${axisX[1]}, 세로 ${axisY[0]}에서 ${axisY[1]}` : undefined}
          onKeyDown={canEdit ? (e) => {
            const step = e.shiftKey ? 10 : 2;
            const cur = myEmotion || { bright: 50, calm: 50 };
            const map = {
              ArrowLeft:  { bright: cur.bright - step },
              ArrowRight: { bright: cur.bright + step },
              ArrowUp:    { calm: cur.calm - step },
              ArrowDown:  { calm: cur.calm + step },
            };
            if (!map[e.key]) return;
            e.preventDefault();
            const next = { ...cur, ...map[e.key] };
            setMyEmotion({
              bright: Math.min(100, Math.max(0, next.bright)),
              calm: Math.min(100, Math.max(0, next.calm)),
            });
          } : undefined}
        >
          <div className="emotion-axis-x" />
          <div className="emotion-axis-y" />
          {/* 아직 등록 전이면 고를 자리를 미리 보여준다 */}
          {canEdit && myEmotion && !emotions[uid] && (
            <div className="emotion-dot emotion-dot-draft"
                 style={{ left: `${myEmotion.bright}%`, top: `${myEmotion.calm}%` }}>🌿</div>
          )}
          {allEmotions.map(([userId, e]) => {
            const isMe = userId === uid;
            const u = (users || []).find(u => u.id === userId);
            return (
              <div
                key={userId}
                className={`emotion-dot${isMe ? " emotion-dot-me" : ""}`}
                style={
                  isMe && canEdit && myEmotion
                    ? { left: `${myEmotion.bright}%`, top: `${myEmotion.calm}%` }
                    : { left: `${e.bright}%`, top: `${e.calm}%` }
                }
                title={isMe ? "나" : (u?.name || "독자")}
              >
                {isMe ? "🌿" : (u?.avatar || "📖")}
              </div>
            );
          })}
        </div>
      </div>
      {canEdit && (
        <div className="emotion-sliders">
          <div className="emotion-hint">
            {myEmotion ? "다시 눌러 옮길 수 있어요" : "좌표를 눌러 이 책의 감정을 표시해보세요"}
          </div>
          <button
            className="btn-emotion-save"
            disabled={!myEmotion}
            onClick={async () => {
              if (!myEmotion) return;
              await setEmotion(book.id, uid, myEmotion.bright, myEmotion.calm);
              if (onMemoAdded) onMemoAdded(book.id);
            }}
          >
            {emotions[uid] ? "수정하기" : "등록하기"}
          </button>
        </div>
      )}
      {allEmotions.length > 0 && (
        <div className="emotion-summary">
          {allEmotions.length}명이 감정을 기록했어요
        </div>
      )}
    </div>
  );
};

export default EmotionCoordinate;
